"use client";

import "client-only";

const AUTH_DOCUMENT_GENERATION_KEY = "iloris.auth-document-generation";
const AUTH_DOCUMENT_SHARED_GENERATION_KEY =
    "iloris.auth-document-generation.shared";
const AUTH_DOCUMENT_BROADCAST_CHANNEL = "iloris.auth-document-transition";
const AUTH_DOCUMENT_HISTORY_STATE_KEY = "__ilorisAuthDocumentGeneration";
const LEGACY_AUTH_DOCUMENT_WINDOW_NAME_PREFIX = "iloris-auth-generation:";
export const AUTH_TRANSITION_PENDING_ATTRIBUTE =
    "data-iloris-auth-transition";

export interface AuthDocumentState {
    generation: string;
    /** A null value is awaiting the fresh document's resolved identity. */
    fingerprint: string | null;
}

interface AuthDocumentFallbackMarker extends AuthDocumentState {
    /**
     * Present only when this tab could not persist the state normally. The
     * marker stays authoritative until a newer shared epoch supersedes it.
     */
    sharedGenerationAtFallback?: string | null;
}

interface PrepareAuthDocumentTransitionRuntime {
    createGeneration: () => string;
    setGeneration: (generation: string) => void;
    publishGeneration?: (generation: string) => void;
    showCurtain: () => void;
}

interface ReloadStaleAuthDocumentRuntime {
    readGeneration: () => string | null;
    showCurtain: () => void;
    reload: () => void;
}

type AuthDocumentGenerationListener = (generation: string) => void;

interface AuthDocumentTransitionSubscriptionRuntime {
    subscribeToStorage: (
        listener: AuthDocumentGenerationListener,
    ) => () => void;
    subscribeToBroadcast: (
        listener: AuthDocumentGenerationListener,
    ) => () => void;
    adoptGeneration: (generation: string) => void;
    readPreparedGeneration?: () => string | null;
    showCurtain: () => void;
    reload: () => void;
}

interface ReconcileAuthDocumentIdentityRuntime {
    createGeneration: () => string;
    readState: () => AuthDocumentState | null;
    persistState: (state: AuthDocumentState) => void;
    publishState: (state: AuthDocumentState) => void;
}

interface AuthDocumentBroadcastMessage {
    state: AuthDocumentState;
    sourceDocumentId: string;
}

interface AuthIdentityInput {
    id: string;
    email?: string | null;
}

let authBroadcastChannel: BroadcastChannel | null | undefined;
let sourceDocumentId: string | undefined;
let inMemoryDocumentMarker: AuthDocumentFallbackMarker | null = null;

function createGeneration(): string {
    if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
    ) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getSourceDocumentId(): string {
    sourceDocumentId ??= createGeneration();
    return sourceDocumentId;
}

function parseAuthDocumentState(rawValue: string | null): AuthDocumentState | null {
    if (!rawValue) return null;

    // Accept the plain generation written by the first release so deploying
    // this protection cannot invalidate an already-open document.
    if (!rawValue.startsWith("{")) {
        return { generation: rawValue, fingerprint: null };
    }

    try {
        const parsed = JSON.parse(rawValue) as Partial<AuthDocumentState>;
        if (
            typeof parsed.generation !== "string" ||
            parsed.generation.length === 0 ||
            (parsed.fingerprint !== null &&
                typeof parsed.fingerprint !== "string")
        ) {
            return null;
        }
        return {
            generation: parsed.generation,
            fingerprint: parsed.fingerprint ?? null,
        };
    } catch {
        return null;
    }
}

function serializeAuthDocumentState(state: AuthDocumentState): string {
    return JSON.stringify(state);
}

function sameAuthDocumentState(
    left: AuthDocumentState | null,
    right: AuthDocumentState,
): boolean {
    return (
        left?.generation === right.generation &&
        left.fingerprint === right.fingerprint
    );
}

function readSharedState(): AuthDocumentState | null {
    try {
        return parseAuthDocumentState(
            window.localStorage.getItem(AUTH_DOCUMENT_SHARED_GENERATION_KEY),
        );
    } catch {
        return null;
    }
}

function readSessionState(): AuthDocumentState | null {
    try {
        return parseAuthDocumentState(
            window.sessionStorage.getItem(AUTH_DOCUMENT_GENERATION_KEY),
        );
    } catch {
        return null;
    }
}

function readPrivateDocumentGeneration(): string | null {
    return (
        readSessionState()?.generation ??
        readDocumentFallbackMarker()?.generation ??
        null
    );
}

function parseAuthDocumentFallbackMarker(
    value: unknown,
): AuthDocumentFallbackMarker | null {
    if (!value || typeof value !== "object") return null;

    const parsed = value as Partial<AuthDocumentFallbackMarker>;
    if (
        typeof parsed.generation !== "string" ||
        parsed.generation.length === 0 ||
        (parsed.fingerprint !== undefined &&
            parsed.fingerprint !== null &&
            typeof parsed.fingerprint !== "string") ||
        ("sharedGenerationAtFallback" in parsed &&
            parsed.sharedGenerationAtFallback !== null &&
            typeof parsed.sharedGenerationAtFallback !== "string")
    ) {
        return null;
    }

    return {
        generation: parsed.generation,
        fingerprint: parsed.fingerprint ?? null,
        ...(Object.prototype.hasOwnProperty.call(
            parsed,
            "sharedGenerationAtFallback",
        )
            ? {
                  sharedGenerationAtFallback:
                      parsed.sharedGenerationAtFallback ?? null,
              }
            : {}),
    };
}

function readHistoryMarker(): AuthDocumentFallbackMarker | null {
    try {
        const state = window.history.state as unknown;
        if (!state || typeof state !== "object") return null;
        return parseAuthDocumentFallbackMarker(
            (state as Record<string, unknown>)[AUTH_DOCUMENT_HISTORY_STATE_KEY],
        );
    } catch {
        return null;
    }
}

function writeDocumentFallbackMarker(
    marker: AuthDocumentFallbackMarker,
): void {
    inMemoryDocumentMarker = marker;

    try {
        if (
            window.name.startsWith(
                LEGACY_AUTH_DOCUMENT_WINDOW_NAME_PREFIX,
            )
        ) {
            window.name = "";
        }
    } catch {
        // A blocked window.name cannot be used for cross-origin disclosure.
    }

    try {
        const currentState = window.history.state as unknown;
        const preservedState =
            currentState && typeof currentState === "object"
                ? (currentState as Record<string, unknown>)
                : {};
        window.history.replaceState(
            {
                ...preservedState,
                [AUTH_DOCUMENT_HISTORY_STATE_KEY]: marker,
            },
            "",
        );
    } catch {
        // The module-local marker still survives App Router navigation/BFCache.
    }
}

function readAndMigrateLegacyWindowMarker(): AuthDocumentFallbackMarker | null {
    let rawMarker: string;
    try {
        if (
            !window.name.startsWith(
                LEGACY_AUTH_DOCUMENT_WINDOW_NAME_PREFIX,
            )
        ) {
            return null;
        }
        rawMarker = window.name.slice(
            LEGACY_AUTH_DOCUMENT_WINDOW_NAME_PREFIX.length,
        );
        // window.name crosses origins. Clear markers written by the prior
        // implementation before this tab can disclose them on navigation.
        window.name = "";
    } catch {
        return null;
    }

    let marker: AuthDocumentFallbackMarker | null = null;
    if (!rawMarker.startsWith("{")) {
        marker = rawMarker
            ? { generation: rawMarker, fingerprint: null }
            : null;
    } else {
        try {
            marker = parseAuthDocumentFallbackMarker(JSON.parse(rawMarker));
        } catch {
            marker = null;
        }
    }

    if (marker) writeDocumentFallbackMarker(marker);
    return marker;
}

function readDocumentFallbackMarker(): AuthDocumentFallbackMarker | null {
    if (inMemoryDocumentMarker) return inMemoryDocumentMarker;

    const historyMarker = readHistoryMarker();
    if (historyMarker) {
        inMemoryDocumentMarker = historyMarker;
        return historyMarker;
    }

    return readAndMigrateLegacyWindowMarker();
}

export function readAuthDocumentState(): AuthDocumentState | null {
    const sharedState = readSharedState();
    const fallbackMarker = readDocumentFallbackMarker();

    if (
        fallbackMarker &&
        "sharedGenerationAtFallback" in fallbackMarker &&
        fallbackMarker.sharedGenerationAtFallback ===
            (sharedState?.generation ?? null)
    ) {
        return {
            generation: fallbackMarker.generation,
            fingerprint: fallbackMarker.fingerprint,
        };
    }

    const state = sharedState ?? readSessionState() ?? fallbackMarker;
    return state
        ? {
              generation: state.generation,
              fingerprint: state.fingerprint,
          }
        : null;
}

export function readAuthDocumentGeneration(): string | null {
    return readAuthDocumentState()?.generation ?? null;
}

function persistAuthDocumentState(
    state: AuthDocumentState,
    writeSharedState: boolean,
): void {
    const sharedStateBeforeWrite = readSharedState();
    let didWriteSessionState = false;
    let didWriteSharedState = !writeSharedState;

    try {
        window.sessionStorage.setItem(
            AUTH_DOCUMENT_GENERATION_KEY,
            serializeAuthDocumentState(state),
        );
        didWriteSessionState = sameAuthDocumentState(
            readSessionState(),
            state,
        );
    } catch {
        // The browsing-context marker below remains a fallback.
    }

    if (writeSharedState) {
        try {
            window.localStorage.setItem(
                AUTH_DOCUMENT_SHARED_GENERATION_KEY,
                serializeAuthDocumentState(state),
            );
            didWriteSharedState = sameAuthDocumentState(
                readSharedState(),
                state,
            );
        } catch {
            // BroadcastChannel still reaches live tabs when storage is blocked.
        }
    } else {
        didWriteSharedState = sameAuthDocumentState(readSharedState(), state);
    }

    writeDocumentFallbackMarker(
        !didWriteSessionState || !didWriteSharedState
            ? {
                  ...state,
                  sharedGenerationAtFallback:
                      sharedStateBeforeWrite?.generation ?? null,
              }
            : state,
    );
}

function getAuthBroadcastChannel(): BroadcastChannel | null {
    if (authBroadcastChannel !== undefined) return authBroadcastChannel;

    try {
        authBroadcastChannel =
            typeof BroadcastChannel === "function"
                ? new BroadcastChannel(AUTH_DOCUMENT_BROADCAST_CHANNEL)
                : null;
    } catch {
        authBroadcastChannel = null;
    }
    return authBroadcastChannel;
}

function publishAuthDocumentState(state: AuthDocumentState): void {
    try {
        getAuthBroadcastChannel()?.postMessage({
            state,
            sourceDocumentId: getSourceDocumentId(),
        } satisfies AuthDocumentBroadcastMessage);
    } catch {
        // localStorage remains the primary cross-tab notification channel.
    }
}

function publishAuthDocumentGeneration(generation: string): void {
    publishAuthDocumentState({ generation, fingerprint: null });
}

function subscribeToStorage(
    listener: AuthDocumentGenerationListener,
): () => void {
    const handleStorage = (event: StorageEvent) => {
        if (event.key !== AUTH_DOCUMENT_SHARED_GENERATION_KEY) return;
        const state = parseAuthDocumentState(event.newValue);
        if (state) listener(state.generation);
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
}

function subscribeToBroadcast(
    listener: AuthDocumentGenerationListener,
): () => void {
    const channel = getAuthBroadcastChannel();
    if (!channel) return () => undefined;

    const handleMessage = (event: MessageEvent<unknown>) => {
        if (!event.data || typeof event.data !== "object") return;
        const message = event.data as Partial<AuthDocumentBroadcastMessage>;
        if (
            !message.state ||
            typeof message.state.generation !== "string" ||
            message.state.generation.length === 0 ||
            message.sourceDocumentId === getSourceDocumentId()
        ) {
            return;
        }
        listener(message.state.generation);
    };
    channel.addEventListener("message", handleMessage);
    return () => channel.removeEventListener("message", handleMessage);
}

function adoptAuthDocumentGeneration(generation: string): void {
    const sharedState = readSharedState();
    persistAuthDocumentState(
        sharedState?.generation === generation
            ? sharedState
            : { generation, fingerprint: null },
        false,
    );
}

function fallbackFingerprint(value: string): string {
    let first = 0x811c9dc5;
    let second = 0x9e3779b9;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        first = Math.imul(first ^ code, 0x01000193) >>> 0;
        second = Math.imul(second ^ code, 0x85ebca6b) >>> 0;
    }
    return `${first.toString(16).padStart(8, "0")}${second
        .toString(16)
        .padStart(8, "0")}`;
}

/** Return an opaque, token-rotation-stable identity marker for cross-tab sync. */
export async function createAuthIdentityFingerprint(
    identity: AuthIdentityInput | null,
): Promise<string> {
    if (!identity) return "anonymous-v1";

    const source = `${identity.id}\u0000${identity.email ?? ""}`;
    try {
        if (globalThis.crypto?.subtle) {
            const encoded = new TextEncoder().encode(source);
            const digest = await globalThis.crypto.subtle.digest(
                "SHA-256",
                encoded,
            );
            const hex = Array.from(new Uint8Array(digest), (byte) =>
                byte.toString(16).padStart(2, "0"),
            ).join("");
            return `account-v1-${hex}`;
        }
    } catch {
        // The deterministic fallback still avoids persisting raw account data.
    }
    return `account-v1-${fallbackFingerprint(source)}`;
}

/**
 * Bind a fresh document to an unresolved Server Action epoch, or advance the
 * epoch when a callback/bootstrap resolves to a different account identity.
 */
export function reconcileAuthDocumentIdentity(
    fingerprint: string,
    runtime: ReconcileAuthDocumentIdentityRuntime = {
        createGeneration,
        readState: readAuthDocumentState,
        persistState: (state) => persistAuthDocumentState(state, true),
        publishState: publishAuthDocumentState,
    },
    expectedMountedGeneration?: string | null,
): { generation: string; advanced: boolean; stale?: true } {
    const currentState = runtime.readState();

    // Fingerprint creation is asynchronous. If another tab advanced the
    // shared epoch while this document was hashing its mounted identity, that
    // identity belongs to the old epoch and must never bind or overwrite the
    // new state. The caller will curtain and reload against the server cookie.
    if (
        expectedMountedGeneration !== undefined &&
        (currentState?.generation ?? null) !== expectedMountedGeneration
    ) {
        return {
            generation:
                currentState?.generation ?? expectedMountedGeneration ?? "",
            advanced: false,
            stale: true,
        };
    }

    // An ordinary fresh document with the same resolved identity only needs
    // to adopt the existing shared state in memory. Rewriting localStorage here
    // could clobber a concurrently published auth transition from another tab.
    if (currentState?.fingerprint === fingerprint) {
        return { generation: currentState.generation, advanced: false };
    }

    const canReuseCurrentGeneration =
        currentState !== null &&
        currentState.fingerprint === null;
    const nextState: AuthDocumentState = canReuseCurrentGeneration
        ? { generation: currentState.generation, fingerprint }
        : { generation: runtime.createGeneration(), fingerprint };

    try {
        runtime.persistState(nextState);
    } catch {
        // Broadcast can still converge live tabs when storage is unavailable.
    }

    if (!canReuseCurrentGeneration || currentState?.fingerprint === null) {
        try {
            runtime.publishState(nextState);
        } catch {
            // A successful shared write still notifies other tabs.
        }
    }

    return {
        generation: nextState.generation,
        advanced: !canReuseCurrentGeneration,
    };
}

export function showAuthTransitionCurtain(): void {
    document.documentElement.setAttribute(
        AUTH_TRANSITION_PENDING_ATTRIBUTE,
        "pending",
    );
}

/**
 * Immediately hide the old account document, then advance both this tab's
 * history marker and the shared same-origin epoch. Persistence and broadcast
 * are independent best-effort channels; neither may block full navigation.
 */
export function prepareAuthDocumentTransition(
    runtime: PrepareAuthDocumentTransitionRuntime = {
        createGeneration,
        setGeneration: (generation) =>
            persistAuthDocumentState(
                { generation, fingerprint: null },
                true,
            ),
        publishGeneration: publishAuthDocumentGeneration,
        showCurtain: showAuthTransitionCurtain,
    },
): void {
    runtime.showCurtain();

    let generation: string;
    try {
        generation = runtime.createGeneration();
    } catch {
        return;
    }

    try {
        runtime.setGeneration(generation);
    } catch {
        // Broadcast remains available when persistence fails unexpectedly.
    }
    try {
        runtime.publishGeneration?.(generation);
    } catch {
        // A successful shared write still notifies other tabs.
    }
}

/** Reload an earlier history document whose auth epoch is no longer current. */
export function reloadStaleAuthDocument(
    mountedGeneration: string | null,
    runtime: ReloadStaleAuthDocumentRuntime = {
        readGeneration: readAuthDocumentGeneration,
        showCurtain: showAuthTransitionCurtain,
        reload: () => window.location.reload(),
    },
): boolean {
    if (runtime.readGeneration() === mountedGeneration) return false;

    runtime.showCurtain();
    runtime.reload();
    return true;
}

/** Subscribe to auth/profile epochs published by other same-origin tabs. */
export function subscribeToAuthDocumentTransitions(
    mountedGeneration: string | null | (() => string | null),
    runtime: AuthDocumentTransitionSubscriptionRuntime = {
        subscribeToStorage,
        subscribeToBroadcast,
        adoptGeneration: adoptAuthDocumentGeneration,
        readPreparedGeneration: readPrivateDocumentGeneration,
        showCurtain: showAuthTransitionCurtain,
        reload: () => window.location.reload(),
    },
): () => void {
    const getMountedGeneration =
        typeof mountedGeneration === "function"
            ? mountedGeneration
            : () => mountedGeneration;
    let isReloading = false;

    const handleGeneration = (incomingGeneration: string) => {
        if (isReloading || incomingGeneration.length === 0) return;
        const currentGeneration = getMountedGeneration();
        if (incomingGeneration === currentGeneration) return;

        // The initiating tab has already persisted this unresolved generation
        // and is about to replace the document. A peer may resolve and publish
        // that same generation first; reloading the initiating tab here would
        // abort its successful login navigation and leave it on /login.
        if (runtime.readPreparedGeneration?.() === incomingGeneration) return;

        isReloading = true;
        runtime.showCurtain();
        try {
            runtime.adoptGeneration(incomingGeneration);
        } catch {
            // The shared epoch or next fresh auth bootstrap stays authoritative.
        }
        runtime.reload();
    };

    let unsubscribeStorage: () => void = () => undefined;
    let unsubscribeBroadcast: () => void = () => undefined;
    try {
        unsubscribeStorage = runtime.subscribeToStorage(handleGeneration);
    } catch {
        // BroadcastChannel may still be available.
    }
    try {
        unsubscribeBroadcast = runtime.subscribeToBroadcast(handleGeneration);
    } catch {
        // A storage event remains sufficient in supported contexts.
    }

    return () => {
        unsubscribeStorage();
        unsubscribeBroadcast();
    };
}
